/**
 * IdleWarningModal — surfaces the "you'll be signed out soon" dialog when the
 * CMSAuthContext idle watchdog crosses the warning threshold (5 min before
 * the 6h auto-logout by default). The dialog:
 *   • Shows a live mm:ss countdown to auto-logout.
 *   • "Stay signed in" resets the idle timer via context.
 *   • "Sign out now" ends the session immediately.
 *
 * The parent CMSAuthProvider is responsible for computing when to open the
 * dialog and for actually logging out — this component is purely presentation.
 */
import { useEffect, useState } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';

const formatMMSS = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const IdleWarningModal = () => {
  const { idleWarning, staySignedIn, logout, getIdleDeadline } = useCMSAuth();
  const [remainingMs, setRemainingMs] = useState(0);

  // Live countdown — 1-second tick, only running while the modal is open.
  useEffect(() => {
    if (!idleWarning) return undefined;
    const tick = () => {
      const deadline = typeof getIdleDeadline === 'function' ? getIdleDeadline() : Date.now();
      setRemainingMs(Math.max(0, deadline - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [idleWarning, getIdleDeadline]);

  const handleSignOut = async () => {
    try { sessionStorage.setItem('cms_last_logout_reason', 'manual'); } catch { /* ignore */ }
    await logout();
    if (window.location.pathname.startsWith('/admin')) {
      window.location.replace('/admin/login');
    }
  };

  return (
    <Dialog
      open={!!idleWarning}
      // Prevent clicking outside from silently dismissing — user must choose.
      onOpenChange={(open) => { if (!open) staySignedIn(); }}
    >
      <DialogContent className="sm:max-w-md" data-testid="idle-warning-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
            <Clock className="w-6 h-6 text-amber-600" />
          </div>
          <DialogTitle data-testid="idle-warning-title">You&apos;ll be signed out soon</DialogTitle>
          <DialogDescription>
            You&apos;ve been inactive for a while. For security we&apos;ll sign you out automatically when the timer hits zero.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 rounded-lg bg-slate-900 text-white text-center py-6" data-testid="idle-warning-countdown-wrapper">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Auto sign-out in</p>
          <p className="font-mono text-4xl font-bold tracking-widest" data-testid="idle-warning-countdown">
            {formatMMSS(remainingMs)}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleSignOut}
            data-testid="idle-signout-btn"
          >
            Sign out now
          </Button>
          <Button
            onClick={staySignedIn}
            className="bg-ux-blue hover:bg-ux-blue/90"
            data-testid="idle-stay-btn"
          >
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IdleWarningModal;
